import { createRef, useState } from 'react';
import { toast } from 'react-toastify';
import { CncEquipmentExt, ToolChangeSignal } from '../../../../shared/types/cncEquipmentExt.ts';
import { Input } from '../../../components/input.tsx';
import { ModalDialog } from '../../../components/modalDialog.tsx';
import { trpc, trpcClient } from '../../../utils/trpc/trpc.ts';


type PartialCncEquipment = Required<Pick<CncEquipmentExt, 'id' | 'programSignal' | 'intervalTargetSignal' |
    'feedOverrideSignal' | 'warmUpSignal' | 'dryRunSignal' | 'partsRequiredSignal' | 'toolChangeSignals'
>>;

interface Props {
    equipment: CncEquipmentExt;
    onEquipmentUpdated: () => void;
    onClose: () => void;
}

export function CncSignalsDialog({
    equipment,
    onEquipmentUpdated,
    onClose,
}: Props) {

    const formRef = createRef<HTMLFormElement>();
    const [programSignal, setProgramSignal] = useState<string>(equipment.programSignal || '');
    const [intervalTargetSignal, setIntervalTargetSignal] = useState<string>(equipment.intervalTargetSignal || '');
    const [feedOverrideSignal, setFeedOverrideSignal] = useState<string>(equipment.feedOverrideSignal || '');
    const [warmUpSignal, setWarmUpSignal] = useState<string>(equipment.warmUpSignal || '');
    const [dryRunSignal, setDryRunSignal] = useState<string>(equipment.dryRunSignal || '');
    const [partsRequiredSignal, setPartsRequiredSignal] = useState<string>(equipment.partsRequiredSignal || '');
    const [toolChangeSignals, setToolChangeSignals] = useState<ToolChangeSignal[]>(equipment.toolChangeSignals || []);
    const [isTestingSignals, setIsTestingSignals] = useState<boolean>(false);

    const mutateCncEquipment = trpc.equipments.updateCnc.useMutation();


    function handleToolChangeSignalChange(index: number, toolChangeSignal: ToolChangeSignal) {
        setToolChangeSignals(prev => {
            const newSignals = [...prev];
            newSignals[index] = toolChangeSignal;
            return newSignals;
        });
    }

    function handleToolChangeSignalAdd(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        setToolChangeSignals(prev => [
            ...prev,
            { targetSignal: '', countSignal: '' }
        ]);
    }

    function handleToolChangeSignalRemove(index: number) {
        setToolChangeSignals(prev => {
            const newSignals = [...prev];
            newSignals.splice(index, 1);
            return newSignals;
        });
    }

    function createCncObject(): PartialCncEquipment {
        return {
            id: equipment.id,
            programSignal: programSignal.trim(),
            intervalTargetSignal: intervalTargetSignal.trim(),
            feedOverrideSignal: feedOverrideSignal.trim(),
            warmUpSignal: warmUpSignal.trim(),
            dryRunSignal: dryRunSignal.trim(),
            partsRequiredSignal: partsRequiredSignal.trim(),
            toolChangeSignals: toolChangeSignals.map(signal => ({
                targetSignal: signal.targetSignal.trim(),
                countSignal: signal.countSignal.trim(),
            })),
        };
    }

    async function handleTestSignals() {
        try {
            setIsTestingSignals(true);

            // Check all signals exist
            const signalsObject = createCncObject();
            const result = await trpcClient.equipments.testCncSignals.query(signalsObject);
            if (result.missingSignals.length == 0) {
                toast.success('All signals are present in MT-LINKi.');
            } else {
                alert(`Some signals are missing in MT-LINKi: ${result.missingSignals.join(', ')}`);
            }
        } catch (error) {
            alert('Failed to test CNC signals.');
        } finally {
            setIsTestingSignals(false);
        }
    }

    async function handleSubmit() {
        const isValidAuto = formRef.current?.reportValidity();
        if (!isValidAuto) {
            return;
        }

        const equipmentPatch = createCncObject();

        // Check duplicate signals
        const signalNames = [
            equipmentPatch.feedOverrideSignal,
            equipmentPatch.warmUpSignal,
            equipmentPatch.dryRunSignal,
            equipmentPatch.partsRequiredSignal,
            ...equipmentPatch.toolChangeSignals.flatMap(it => [it.targetSignal, it.countSignal])
        ].filter(Boolean);
        const duplicates = [...new Set(signalNames.filter((v, i, a) => a.indexOf(v) !== i))];
        if (duplicates.length > 0) {
            alert(`Duplicate signals found: ${duplicates.join(', ')}`);
            return;
        }

        try {
            await mutateCncEquipment.mutateAsync(equipmentPatch);
        } catch (error) {
            alert('Failed to save CNC equipment.');
            return;
        }

        onEquipmentUpdated();
        onClose();
    }

    const isSubmitting = mutateCncEquipment.isPending;
    return (
        <ModalDialog
            frameStyle={{ width: '750px' }}
            isOpen={true}
            onCancel={onClose}
        >
            <form ref={formRef} >
                <h3 style={{ marginTop: '0px' }}>
                    {equipment.displayName} - {equipment.id}
                </h3>
                <SingleSignalInput
                    label='Program name'
                    value={programSignal}
                    onChange={e => setProgramSignal(e.target.value)}
                />
                <SingleSignalInput
                    label='Measurement interval target'
                    value={intervalTargetSignal}
                    onChange={e => setIntervalTargetSignal(e.target.value)}
                />
                <SingleSignalInput
                    label='Feed override'
                    value={feedOverrideSignal}
                    onChange={e => setFeedOverrideSignal(e.target.value)}
                />
                <SingleSignalInput
                    label='Warm up'
                    value={warmUpSignal}
                    onChange={e => setWarmUpSignal(e.target.value)}
                />
                <SingleSignalInput
                    label='Dry run'
                    value={dryRunSignal}
                    onChange={e => setDryRunSignal(e.target.value)}
                />
                <SingleSignalInput
                    label='Parts required'
                    value={partsRequiredSignal}
                    onChange={e => setPartsRequiredSignal(e.target.value)}
                />


                <div className='dialog-form__field--with-margins'  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                        <span>Tool change signals: target and count</span>
                        <button onClick={handleToolChangeSignalAdd}>Add</button>
                    </div>
                    {toolChangeSignals.map((toolChangeSignal, index) => (
                        <ToolChangeRow
                            key={index}
                            index={index}
                            toolChangeSignal={toolChangeSignal}
                            onChange={handleToolChangeSignalChange}
                            onRemoveClick={handleToolChangeSignalRemove}
                        />
                    ))}
                </div>
            </form>

            <div className='dialog--button-bar'>
                <button onClick={handleTestSignals} disabled={isTestingSignals}>Test Signals</button>
                <span className='flex__spacer' />
                <button onClick={onClose}>Cancel</button>
                <button onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
            </div>
        </ModalDialog>
    );
}


function SingleSignalInput({
    label,
    value,
    onChange
}: {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
    return (
        <div className='dialog-form__field--with-margins'>
            <div>{label}</div>
            <Input
                className='dialog-form__input'
                type='text'
                value={value}
                onChange={onChange}
            />
        </div>
    );
}


function ToolChangeRow({
    index,
    toolChangeSignal,
    onChange,
    onRemoveClick,
}: {
    index: number;
    toolChangeSignal: ToolChangeSignal;
    onChange: (index: number, toolChangeSignal: ToolChangeSignal) => void;
    onRemoveClick: (index: number) => void;
}) {

    function handleChange(field: keyof ToolChangeSignal, value: string) {
        onChange(index, { ...toolChangeSignal, [field]: value });
    }

    function handleRemoveClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        onRemoveClick(index);
    }

    return (
        <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
            <Input
                style={{ width: '100%' }}
                type='text'
                value={toolChangeSignal.targetSignal}
                onChange={e => handleChange('targetSignal', e.target.value)}
                required
            />
            <Input
                style={{ width: '100%' }}
                type='text'
                value={toolChangeSignal.countSignal}
                onChange={e => handleChange('countSignal', e.target.value)}
                required
            />
            <button onClick={handleRemoveClick}>Remove</button>
        </div>
    );
}
